import { Dialog, DialogProps } from "@mui/material";
import {
  Form,
  Formik,
  FormikConfig,
  FormikProps,
  FormikValues,
  useFormikContext,
} from "formik";
import { omit } from "lodash";
import { ReactElement, useEffect } from "react";

import usePrevious from "~frontend/util/usePrevious";

// These props cause React warnings if they are passed to the DOM, so we
// prohibit passing them to the Dialog
const formOnlyProps = ["validationSchema", "initialValues"];

export function FormikDialog<Values extends FormikValues>({
  children,
  onSubmit,
  ...otherProps
}: Omit<DialogProps, "children" | "onSubmit"> &
  FormikConfig<Values>): ReactElement {
  const formikChildren =
    children instanceof Function ? (
      (args: FormikProps<Values>) => (
        <FormikDialogInner {...otherProps}>{children(args)}</FormikDialogInner>
      )
    ) : (
      <FormikDialogInner {...otherProps}>{children}</FormikDialogInner>
    );

  return (
    <Formik<Values>
      {...otherProps}
      onSubmit={onSubmit}
      // enableReinitialize causes updates to the initialValues to propagate
      // through the form context provider to the form children. This is
      // necessary so that when the dialog is opened and the form is reset it
      // will be reset to the current initialValues.
      enableReinitialize
    >
      {formikChildren}
    </Formik>
  );
}

function FormikDialogInner<Values extends FormikValues>({
  children,
  open,
  ...otherProps
}: Omit<DialogProps, "onSubmit">) {
  const form = useFormikContext<Values>();

  const previouslyOpen = usePrevious(open);
  useEffect(() => {
    if (open && !previouslyOpen) {
      form.resetForm();
    }
  }, [open, previouslyOpen, form]);

  return (
    <Dialog
      open={open}
      PaperProps={{
        component: Form,
      }}
      {...omit(otherProps, formOnlyProps)}
    >
      {children}
    </Dialog>
  );
}
